import { MailCheckIcon } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { requestPasswordReset } from '@/api/users'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthShell } from '@/components/layout/AuthShell'
import { isEmail } from '@/components/users/validation'

/**
 * Public — somebody who has forgotten their password has no token, which is the whole point.
 *
 * Keycloak's own login page carries the same link; this screen exists so the flow is reachable
 * from our own UI (and from the iPhone app, against the same endpoint) without sending the user
 * to a page that looks like a different product.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) {
      return
    }
    const address = email.trim()
    if (!address) {
      setError(t('validation.required'))
      return
    }
    if (!isEmail(address)) {
      setError(t('validation.email'))
      return
    }
    setError(null)
    setFailure(null)
    setSubmitting(true)
    try {
      await requestPasswordReset(address)
      // The backend answers the same way whether or not the address has an account, and so does
      // this screen — telling the two apart is exactly what it must not offer.
      setSentTo(address)
    } catch {
      // Only a request that never reached the backend gets here; it answers 202 for everything
      // else, on purpose.
      setFailure(t('forgotPassword.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card>
        {sentTo ? (
          <>
            <CardHeader>
              <MailCheckIcon className="text-muted-foreground mb-1 size-5" aria-hidden />
              <CardTitle>{t('forgotPassword.sentTitle')}</CardTitle>
              <CardDescription>
                {t('forgotPassword.sentDescription', { email: sentTo })}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              <Button asChild className="w-full">
                <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setSentTo(null)
                  setEmail('')
                }}
              >
                {t('forgotPassword.again')}
              </Button>
            </CardContent>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <CardHeader>
              <CardTitle>{t('forgotPassword.title')}</CardTitle>
              <CardDescription>{t('forgotPassword.description')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              {failure && (
                <Alert variant="destructive">
                  <AlertTitle>{t('forgotPassword.title')}</AlertTitle>
                  <AlertDescription>{failure}</AlertDescription>
                </Alert>
              )}
              <div className="grid gap-2">
                <Label htmlFor="forgot-email">{t('forgotPassword.email')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'forgot-email-error' : undefined}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {error && (
                  <p id="forgot-email-error" className="text-destructive text-xs">
                    {error}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Button type="submit" className="w-full" disabled={submitting}>
                  {t('forgotPassword.submit')}
                </Button>
                <Button asChild variant="ghost" className="w-full">
                  <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
                </Button>
              </div>
            </CardContent>
          </form>
        )}
      </Card>
    </AuthShell>
  )
}
